import React, { useState, useEffect } from 'react'
import * as Yup from 'yup'
import * as Sentry from '@sentry/nextjs'
import {
  gql,
  MutationFunction,
  useLazyQuery,
  useMutation
} from '@apollo/client'
import { ON_SERVER } from 'shared/enums/environment'
import { useRouter } from 'next/router'
import { H3, Text } from 'library/atoms/Typography'
import Button from 'library/atoms/Button'
import styled from '@emotion/styled'
import useTranslation from 'next-translate/useTranslation'
import {
  Basket_Response,
  Redemption_Codes
} from 'shared/presenters/graphqlTypes'
import { Formik, Form } from 'formik'
import Input from 'library/atoms/Input'
import { RequestToRedeemCodeResult } from 'state/useAssignPriceModifier'
import InfoBar from 'library/molecules/InfoBar'
import { RedemptionCodePresenter } from 'presenters'
import { definedNotNull, RecursivePartial } from 'shared/presenters/presenter'
import Price, { PriceText, PriceWrapper } from 'shared/library/atoms/Price'
import { OutletPresenter } from 'shared/presenters'
import { AnimatedIcon } from 'shared/library/atoms/Icon'
import Flex from 'shared/library/atoms/Flex'
import widgetRoutes from 'enums/routes'
import { REMOVE_BASKET_ITEM } from 'library/organisms/Basket'
import { FETCH_BASKET } from 'providers/BasketProvider'
import { HttpHeader } from 'shared/enums/httpHeader'

export interface ModalContentRedeemVoucherInterface
  extends RequestToRedeemCodeResult {
  onClose: () => void
  outletId: string
  outlet: OutletPresenter
  basketId: Basket_Response['basket_id']
  removePriceModifier: MutationFunction
}

interface RedeemCodeField {
  code: string
}

const REDEEM_CODE_INITIAL_VALUE: RedeemCodeField = {
  code: ''
}

const FETCH_VOUCHER_DATA = gql`
  query FetchVouchers($widgetSlug: String, $code: String) {
    widgets(where: { slug: { _eq: $widgetSlug } }) {
      filter_by_category
    }

    redemption_codes(where: { code: { _eq: $code } }) {
      code
      id
      target_type
      credit_voucher {
        amount_pence
        remaining_pence
      }
      purchased_class_pass {
        redemptions_remaining
        class_pass {
          class_passes_activities {
            schedulable {
              id
            }
          }
        }
      }
      activity_voucher {
        booking_id
        id
        booking {
          line_item {
            id
            basket_id
          }
          id
          schedulable {
            slug
          }
        }
      }
      discount_code {
        id
        percent
      }
    }
  }
`

/**
 * Use this component to insert a code and redeem a credit voucher, activity voucher or class pass.
 */

const ModalContentRedeemVoucher = ({
  onClose,
  outletId,
  outlet,
  basketId,
  redeemCode,
  redeemCodeLoading,
  removePriceModifier
}: ModalContentRedeemVoucherInterface) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const locale = router.locale
  const widgetSlug = router?.query?.widgetSlug as string
  const [inputCode, setInputCode] = useState('')
  const [voucherResult, setVoucherResult] = useState<
    RedemptionCodePresenter[] | undefined
  >(undefined)

  const [errorMessage, setErrorMessage] = useState('')

  /**
   * Validate field
   *
   * We allow centres to make up custom codes. It is best to keep this kind of validation in one place on the backend.
   * The endpoint should give a helpful error if it’s invalid so you can just display that directly
   */
  const RedeemCodeValidateSchema = Yup.object().shape({
    code: Yup.string()
      .required(t('required'))
      .matches(/^[\w-_]+$/, t('no-special-characters'))
  })

  const [fetchVoucher, { data }] = useLazyQuery<{
    redemption_codes: Redemption_Codes[]
  }>(FETCH_VOUCHER_DATA, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    context: { headers: { [HttpHeader.xHasuraRedemptionCode]: inputCode } },
    ssr: ON_SERVER
  })

  useEffect(() => {
    if (!data) return

    const reedemCodeFunction = async () => {
      const redemption_codes = data.redemption_codes

      if (!redemption_codes.length) {
        setErrorMessage(
          'Invalid code: Please check you are entering the right code'
        )
        return
      }

      const redemptionCode = redemption_codes?.map(
        (code: RecursivePartial<Redemption_Codes>) =>
          new RedemptionCodePresenter(code)
      )

      setVoucherResult(redemptionCode)
      try {
        await redeemCode({
          variables: {
            basketId,
            code: redemptionCode?.[0]?.code,
            // The item id isn't necessary for credit voucher, bring it back when we are dealing with something else that needs it.
            // lineItemId: itemId,
            outletId,
            widgetSlug
          }
        })
      } catch (err) {
        if (err instanceof Error) {
          setErrorMessage(err.message)
        }
      }
    }
    reedemCodeFunction()
  }, [data])

  const redeemCodeOnClick = async (code: string) => {
    const redeemCodeVars = {
      variables: {
        widgetSlug,
        code,
        locale,
        basketId
      }
    }
    try {
      await fetchVoucher(redeemCodeVars)
    } catch (err) {
      // No need to handle this error
      // The error message is already assigned to errorMessage
    }
  }

  const removePriceModifierOnClick = async ({
    basketId,
    code,
    removePriceModifier
  }: {
    basketId?: Basket_Response['basket_id']
    code: Redemption_Codes['code'] | undefined
    removePriceModifier: MutationFunction
  }) => {
    if (!basketId) {
      return
    }
    try {
      await removePriceModifier({
        variables: {
          // TODO: The lineItemId isn't necessary for removing a credit voucher
          // Uncomment once we are dealing with more price_modifiers that uses this id to remove the discount
          // lineItemId: itemId,
          basketId,
          code
        }
      })
      setVoucherResult(undefined)
    } catch (err) {
      // We have to verify err is an
      // error before using it as one.
      if (err instanceof Error) {
        Sentry.captureMessage(err.message)
      }
    }
  }

  const [removeBasketItem] = useMutation<{
    Consumer_Baskets_RemoveItem: Basket_Response
  }>(REMOVE_BASKET_ITEM, {
    refetchQueries: [
      {
        query: FETCH_BASKET,
        variables: { basketId, locale },
        context: { headers: { [HttpHeader.xHasuraBasketId]: basketId } }
      }
    ]
  })

  const removeBasketItemOnClick = async (lineItemId: string) => {
    if (!basketId) {
      return
    }

    try {
      await removeBasketItem({
        variables: {
          lineItemId
        }
      })
    } catch (err) {
      // catching here to protect against page breaks
    }
  }

  const onSubmit = async (values: RedeemCodeField) => {
    await setInputCode(values.code.toLowerCase())
    await redeemCodeOnClick(values.code.toLowerCase())
  }

  const handleOnClose = () => {
    setVoucherResult(undefined)
    onClose()
  }

  const voucher = voucherResult?.[0]
  const creditVoucher = voucher?.credit_voucher
  const activityVoucher = voucher?.activity_voucher
  const discountCode = voucher?.discount_code
  const classPass = voucher?.purchased_class_pass
  const activityVoucherLineItemId =
    voucher?.activity_voucher?.booking?.line_item?.id

  return (
    <>
      {!voucherResult || voucherResult?.length < 1 ? (
        <Formik
          initialValues={REDEEM_CODE_INITIAL_VALUE}
          onSubmit={onSubmit}
          validationSchema={RedeemCodeValidateSchema}
        >
          {({ handleSubmit, values }) => (
            <StyledDiv>
              <StyledForm>
                <H3 mb={4}>{t('redeem-voucher')}</H3>
                <Text mb={5}>{t('redeeming-explanation')}</Text>
                <StyledField active={values.code.length < 1}>
                  <Input
                    label={t('code')}
                    placeholder="123456"
                    name="code"
                    onChange={() => {
                      setErrorMessage('')
                    }}
                  />
                </StyledField>
                {errorMessage && values.code.length > 1 && (
                  <InfoBar
                    variant="error"
                    icon="info"
                    size="small"
                    mb={4}
                    mt={4}
                  >
                    {errorMessage}
                  </InfoBar>
                )}
                <StyledVoucherDirections>
                  {t('code-directions')}
                </StyledVoucherDirections>
                <Button
                  type="submit"
                  onClick={handleSubmit}
                  inactive={values.code.length < 1}
                  loading={redeemCodeLoading}
                  mt={5}
                  aria-label={t('find-voucher')}
                >
                  {t('enter')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleOnClose}
                  mt={4}
                  aria-label={t('close-redeem-voucher')}
                >
                  {t('close')}
                </Button>
              </StyledForm>
            </StyledDiv>
          )}
        </Formik>
      ) : (
        <StyledDiv>
          <AnimatedIcon name="clap" size={64} autoplay m="auto" />
          <H3>
            {t(
              !discountCode
                ? 'common:voucher-found'
                : 'common:discount-code-found'
            )}
          </H3>
          {discountCode && (
            <>
              {/* TODO - Ask Jamie if to confirm if he wants percentage in bold */}
              <Text fontStyle="p2">
                {t('x-percent-discount-code', {
                  count: discountCode?.percent
                })}
              </Text>
              <Text fontStyle="p2">{t('would-you-like-to-use-voucher')}</Text>
            </>
          )}
          {creditVoucher && (
            <>
              <Flex gap={1}>
                <Text fontStyle="p2">{t('you-have')}</Text>
                <PriceWrapper>
                  <Price
                    pence={definedNotNull(creditVoucher?.remaining_pence)}
                    currency={outlet.currency()}
                    color="secondary"
                  />
                  <PriceText fontStyle="p2" color="secondary">
                    {' '}
                    {t('credit')}
                  </PriceText>
                </PriceWrapper>
              </Flex>

              <Text fontStyle="p2">{t('would-you-like-to-use-voucher')}</Text>
              <Button
                type="submit"
                onClick={handleOnClose}
                loading={redeemCodeLoading}
                mt={6}
                inactive={!!errorMessage}
              >
                {t('redeem-at-checkout')}
              </Button>
            </>
          )}

          {activityVoucher && (
            <>
              <Text fontStyle="p2">{t('you-have-a-gift')}</Text>
              <Button
                type="submit"
                onClick={() => {
                  router.push(
                    widgetRoutes.ACTIVITY.url({
                      widgetSlug,
                      /**
                       * Comment on categorySlug - We feed in activityVoucher?.booking_id as the cateogry slug here to keep the current URL structure and redirect user into same flow as booking normal activity,
                       * we then conditionally hide / show  components based on whether the booking is from an activity voucher - see ActivityConfig.tsx, this was done to enhance re-use of same components throughout the flow.
                       */
                      categorySlug: activityVoucher?.booking_id as string,
                      activitySlug: activityVoucher?.booking?.schedulable
                        ?.slug as string
                    })
                  )
                  handleOnClose()
                }}
                loading={redeemCodeLoading}
                mt={6}
                inactive={!!errorMessage}
              >
                {t('redeem-gift')}
              </Button>
            </>
          )}

          {(classPass || discountCode) && (
            <Button
              type="submit"
              onClick={handleOnClose}
              loading={redeemCodeLoading}
              mt={6}
              inactive={!!errorMessage}
            >
              {t('redeem-at-checkout')}
            </Button>
          )}

          <Button
            type="submit"
            variant="secondary"
            onClick={() => {
              if (creditVoucher || discountCode) {
                removePriceModifierOnClick({
                  basketId,
                  code: inputCode,
                  removePriceModifier
                })
              }
              if (activityVoucher && activityVoucherLineItemId) {
                removeBasketItemOnClick(activityVoucherLineItemId)
              }
              handleOnClose()
            }}
            loading={redeemCodeLoading}
            mt={4}
            aria-label={t('cancel-redeem-voucher')}
          >
            {t('cancel')}
          </Button>
          {!redeemCodeLoading && errorMessage && (
            <InfoBar variant="error" icon="info" size="small" mb={4} mt={4}>
              {errorMessage}
            </InfoBar>
          )}
        </StyledDiv>
      )}
    </>
  )
}

export default ModalContentRedeemVoucher

const StyledForm = styled(Form)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  ...theme.fontStyle.p3,
  p: {
    textAlign: 'center',
    color: theme.colors.text.secondary
  }
}))

const StyledDiv = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  flexDirection: 'column',
  width: '100%',
  padding: `${theme.space[5]}px ${theme.space[2]}px`,
  ...theme.fontStyle.p3,
  p: {
    textAlign: 'center',
    color: theme.colors.text.secondary
  },
  button: {
    width: '100%'
  }
}))

const StyledField = styled.div<{ active: boolean }>(({ theme, active }) => ({
  position: 'relative',
  '&::before': {
    content: '"#"',
    position: 'absolute',
    left: 15,
    top: 42, // fixed top to prevent the button moving if the input needs to show some error
    display: 'block',
    color: active ? theme.colors.text.secondary : theme.colors.text.primary,
    ...theme.fontStyle.p2
  },
  input: {
    paddingLeft: theme.space[6],
    marginTop: 0,
    ...theme.fontStyle.p2,
    textAlign: 'left'
  },
  label: {
    textAlign: 'left'
  },
  p: {
    textAlign: 'left',
    color: theme.colors.text.secondary
  }
}))

const StyledVoucherDirections = styled.div(({ theme }) => ({
  height: theme.space[9],
  textAlign: 'center',
  marginTop: theme.space[4],
  padding: theme.space[3],
  color: theme.colors.text.secondary,
  background: theme.colors.background.secondary,
  ...theme.fontStyle.p3
}))
